import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileOrUrlField } from "@/components/common/FileOrUrlField";
import { Plus, Pencil, UserMinus, Upload, Search, CheckCircle2, Users as UsersIcon } from "lucide-react";

const emptyMember = { name_en: "", name_gu: "", district_slug: "", mobile: "", email: "", designation_en: "Medical Officer (Ayurved)", taluka: "", dispensary: "" };
const statusColor = { green: "bg-gmoaa-green-light text-gmoaa-green", red: "bg-gmoaa-saffron-light text-gmoaa-saffron-hover", grey: "bg-gray-200 text-gray-500" };

export const MembersSection = () => {
  const [rows, setRows] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [q, setQ] = useState("");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [verifFilter, setVerifFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [retYearFilter, setRetYearFilter] = useState("all");
  const [filterOpts, setFilterOpts] = useState({ batches: [], retirement_years: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyMember);
  const [editId, setEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [leadersOpen, setLeadersOpen] = useState(false);
  const [bulkDistrict, setBulkDistrict] = useState("");

  const load = useCallback(() => {
    adminApi.members({
      q: q || undefined, district: districtFilter, verif: verifFilter,
      batch: batchFilter === "all" ? undefined : batchFilter,
      retirement_year: retYearFilter === "all" ? undefined : retYearFilter,
    }).then(setRows);
  }, [q, districtFilter, verifFilter, batchFilter, retYearFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.districts().then(setDistricts); }, []);
  useEffect(() => { adminApi.serviceFilterOptions().then(setFilterOpts).catch(() => {}); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setDistrict = (slug) => setForm((f) => ({ ...f, district_slug: slug, taluka: "" }));
  const selDistrict = districts.find((d) => d.slug === form.district_slug);
  const talukaOptions = selDistrict?.talukas || [];
  const openNew = () => { setForm(emptyMember); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm(m); setEditId(m.id); setOpen(true); };

  const save = async () => {
    if (!form.name_en || !/^\d{10}$/.test(form.mobile || "")) { toast.error("Name and a valid 10-digit mobile are required"); return; }
    try {
      if (editId) await adminApi.updateMember(editId, form);
      else await adminApi.createMember(form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (m) => {
    const reason = window.prompt("Reason for removal (transferred/retired/resigned/duplicate/other):", "transferred");
    if (!reason) return;
    await adminApi.removeMember(m.id, reason); toast.success("Member set to grey (removed)"); load();
  };

  const bulkAssign = async () => {
    if (!bulkDistrict) { toast.error("Pick a district to assign"); return; }
    const targets = rows.filter((m) => !m.district_slug);
    if (targets.length === 0) { toast.message("No members without a district"); return; }
    if (!window.confirm(`Assign ${targets.length} member(s) to this district?`)) return;
    try {
      await Promise.all(targets.map((m) => adminApi.updateMember(m.id, { district_slug: bulkDistrict })));
      toast.success(`Assigned ${targets.length} member(s)`); setBulkDistrict(""); load();
    } catch { toast.error("Bulk assign failed"); }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Member Roster ({rows.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLeadersOpen(true)} className="border-gmoaa-border-subtle"><UsersIcon size={16} className="mr-1" /> Leaders</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} className="border-gmoaa-border-subtle"><Upload size={16} className="mr-1" /> Import</Button>
          <Button data-testid="member-add-btn" onClick={openNew} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> Add</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gmoaa-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or mobile..." className="border-gmoaa-border-subtle bg-white pl-9" />
        </div>
        <Select value={districtFilter} onValueChange={setDistrictFilter}>
          <SelectTrigger className="w-48 border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            <SelectItem value="none">⚠ No district</SelectItem>
            {districts.map((d) => <SelectItem key={d.id} value={d.slug}>{d.name_en}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={verifFilter} onValueChange={setVerifFilter}>
          <SelectTrigger data-testid="roster-verif-filter" className="w-48 border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any verification</SelectItem>
            <SelectItem value="pending">Pending verification</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="none">No service details</SelectItem>
          </SelectContent>
        </Select>
        <Select value={batchFilter} onValueChange={setBatchFilter}>
          <SelectTrigger data-testid="roster-batch-filter" className="w-40 border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any batch</SelectItem>
            {filterOpts.batches.map((b) => <SelectItem key={b} value={b}>Batch {b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={retYearFilter} onValueChange={setRetYearFilter}>
          <SelectTrigger data-testid="roster-retyear-filter" className="w-44 border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any retirement year</SelectItem>
            {filterOpts.retirement_years.map((y) => <SelectItem key={y} value={y}>Retires {y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {districtFilter === "none" && rows.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-3 rounded-xl border border-gmoaa-red/30 bg-gmoaa-red-bg px-4 py-3" data-testid="no-district-warning">
            <p className="flex-1 text-sm text-gmoaa-red">{rows.length} member(s) have <b>no district</b>. Assign them in bulk:</p>
            <Select value={bulkDistrict} onValueChange={setBulkDistrict}>
              <SelectTrigger data-testid="bulk-district-select" className="w-48 border-gmoaa-border-subtle bg-white"><SelectValue placeholder="Select district" /></SelectTrigger>
              <SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.slug}>{d.name_en}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="bulk-assign-btn" size="sm" onClick={bulkAssign} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Assign district</Button>
          </div>
        )}
        {rows.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium text-gmoaa-text-dark">
                {m.name_en}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusColor[m.status] || ""}`}>{m.status}</span>
              </p>
              <p className="text-xs text-gmoaa-text-muted">{m.member_id} · {m.district_slug} · {m.mobile}</p>
              {m.service_summary?.has_service && (
                <p className="mt-0.5 text-[11px] text-gmoaa-text-muted">
                  {m.service_summary.batch && <span>Batch {m.service_summary.batch} · </span>}
                  {m.service_summary.joining_year && <span>Joined {m.service_summary.joining_year} · </span>}
                  {m.service_summary.retirement_year && <span>Retires {m.service_summary.retirement_year} · </span>}
                  <span className={m.service_summary.verification_status === "verified" ? "text-gmoaa-green" : m.service_summary.verification_status === "pending" ? "text-gmoaa-saffron-hover" : "text-gmoaa-red"}>{m.service_summary.verification_status}</span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Pencil size={15} /></Button>
              {m.status !== "grey" && <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => remove(m)}><UserMinus size={15} /></Button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-gmoaa-text-muted">No members match.</p>}
      </div>

      {/* Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-gmoaa-cream">
          <DialogHeader><DialogTitle className="text-gmoaa-green">{editId ? "Edit" : "Add"} member</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Name (English)" value={form.name_en} onChange={set("name_en")} testid="member-form-name-en" />
            <Field label="Name (Gujarati)" value={form.name_gu} onChange={set("name_gu")} />
            <Field label="Mobile (profile, 10 digits)" value={form.mobile} onChange={set("mobile")} testid="member-form-mobile" />
            <Field label="Email (login — Google / email code)" value={form.email} onChange={set("email")} />
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">District</p>
              <Select value={form.district_slug} onValueChange={setDistrict}>
                <SelectTrigger data-testid="member-form-district" className="border-gmoaa-border-subtle bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.slug}>{d.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Designation" value={form.designation_en} onChange={set("designation_en")} />
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Taluka</p>
              <Select value={form.taluka || ""} onValueChange={set("taluka")} disabled={!form.district_slug}>
                <SelectTrigger data-testid="member-form-taluka" className="border-gmoaa-border-subtle bg-white"><SelectValue placeholder={form.district_slug ? "Select taluka" : "Select district first"} /></SelectTrigger>
                <SelectContent>
                  {talukaOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  {form.taluka && !talukaOptions.includes(form.taluka) && <SelectItem value={form.taluka}>{form.taluka}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <Field label="Dispensary / posting" value={form.dispensary} onChange={set("dispensary")} />
          </div>
          <div className="mt-3"><FileOrUrlField label="Profile photo" accept="image" value={form.photo_url} onChange={set("photo_url")} testid="member-form-photo" /></div>
          <DialogFooter><Button data-testid="member-form-save" onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <LeadersDialog open={leadersOpen} onOpenChange={setLeadersOpen} districts={districts} />
    </div>
  );
};

const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [text, setText] = useState("name_en,mobile,district_slug,email\nDr. Test Officer,9111111111,ahmedabad,test@example.com");
  const [mode, setMode] = useState("upsert");
  const [preview, setPreview] = useState(null);

  const parse = () => {
    const lines = text.trim().split("\n").filter(Boolean);
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((ln) => {
      const cells = ln.split(",");
      const o = {};
      headers.forEach((h, i) => { o[h] = (cells[i] || "").trim(); });
      return o;
    });
  };

  const doPreview = async () => {
    try { setPreview(await adminApi.importMembers({ rows: parse(), mode, dry_run: true })); }
    catch { toast.error("Could not parse / preview"); }
  };
  const apply = async () => {
    try { const r = await adminApi.importMembers({ rows: parse(), mode, dry_run: false }); toast.success(`Applied: ${r.applied} (new ${r.new}, updated ${r.updated})`); setPreview(null); onOpenChange(false); onDone(); }
    catch { toast.error("Import failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-gmoaa-cream">
        <DialogHeader><DialogTitle className="text-gmoaa-green">Import members (CSV paste)</DialogTitle></DialogHeader>
        <p className="text-xs text-gmoaa-text-muted">First row = headers. Supported: name_en, name_gu, mobile, district_slug, email, designation_en, taluka, dispensary.</p>
        <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} className="border-gmoaa-border-subtle bg-white font-mono text-xs" />
        <div className="flex items-center gap-3">
          <Label className="text-sm">Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-56 border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="upsert">Update existing + add new</SelectItem><SelectItem value="add">Add new only</SelectItem></SelectContent>
          </Select>
          <Button variant="outline" onClick={doPreview} className="border-gmoaa-border-subtle">Preview</Button>
        </div>
        {preview && (
          <div className="rounded-lg border border-gmoaa-border-subtle bg-white p-3 text-sm">
            <p className="font-medium text-gmoaa-text-dark">New: {preview.new} · Updated: {preview.updated} · Skipped: {preview.skipped}</p>
            {preview.warnings?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-gmoaa-saffron-hover">{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
          </div>
        )}
        <DialogFooter><Button onClick={apply} disabled={!preview} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Apply import</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LeadersDialog = ({ open, onOpenChange, districts }) => {
  const [slug, setSlug] = useState("");
  const [members, setMembers] = useState([]);
  const [coord, setCoord] = useState("");
  const [pres, setPres] = useState("");

  useEffect(() => {
    if (!slug) return;
    adminApi.members({ district: slug }).then(setMembers);
    const d = districts.find((x) => x.slug === slug);
    setCoord(d?.coordinator_member_id || ""); setPres(d?.president_member_id || "");
  }, [slug, districts]);

  const save = async () => {
    try { await adminApi.setLeaders(slug, { coordinator_member_id: coord || null, president_member_id: pres || null }); toast.success("Leaders updated"); onOpenChange(false); }
    catch { toast.error("Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gmoaa-cream">
        <DialogHeader><DialogTitle className="text-gmoaa-green">Assign district leaders</DialogTitle></DialogHeader>
        <div>
          <Label className="mb-1 block text-xs text-gmoaa-text-muted">District</Label>
          <Select value={slug} onValueChange={setSlug}>
            <SelectTrigger className="border-gmoaa-border-subtle bg-white"><SelectValue placeholder="Select district" /></SelectTrigger>
            <SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.slug}>{d.name_en}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {slug && (
          <>
            <div>
              <Label className="mb-1 block text-xs text-gmoaa-text-muted">District President of Union</Label>
              <Select value={pres} onValueChange={setPres}>
                <SelectTrigger className="border-gmoaa-border-subtle bg-white"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs text-gmoaa-text-muted">District Coordinator</Label>
              <Select value={coord} onValueChange={setCoord}>
                <SelectTrigger className="border-gmoaa-border-subtle bg-white"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
